'use client';

import { useMemo, useState } from 'react';

const PRESETS = [10, 15, 18, 20, 25];

export function TipCalculator() {
  const [bill, setBill] = useState('64.00');
  const [tipPercent, setTipPercent] = useState(18);
  const [customTip, setCustomTip] = useState('');
  const [people, setPeople] = useState('2');

  const activeTip = customTip ? Number.parseFloat(customTip) : tipPercent;

  const result = useMemo(() => {
    const billAmount = Number.parseFloat(bill);
    const n = Math.max(1, Number.parseInt(people, 10) || 1);
    if (!Number.isFinite(billAmount) || !Number.isFinite(activeTip)) return null;
    const tipAmount = billAmount * (activeTip / 100);
    const total = billAmount + tipAmount;
    return { tipAmount, total, perPerson: total / n };
  }, [bill, activeTip, people]);

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-xs font-medium text-muted-foreground">Bill amount</span>
          <input
            type="number"
            step="0.01"
            name="bill-amount"
            autoComplete="off"
            value={bill}
            onChange={(e) => setBill(e.target.value)}
            className="mt-1 h-11 w-full rounded-lg border border-input bg-background px-3 text-lg text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-muted-foreground">Number of people</span>
          <input
            type="number"
            min="1"
            name="people-count"
            autoComplete="off"
            value={people}
            onChange={(e) => setPeople(e.target.value)}
            className="mt-1 h-11 w-full rounded-lg border border-input bg-background px-3 text-lg text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
          />
        </label>
      </div>

      <div className="mt-4">
        <span className="text-xs font-medium text-muted-foreground">Tip percentage</span>
        <div className="mt-1.5 flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => {
                setTipPercent(p);
                setCustomTip('');
              }}
              className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                !customTip && tipPercent === p
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground'
              }`}
            >
              {p}%
            </button>
          ))}
          <input
            type="number"
            placeholder="Custom"
            name="custom-tip"
            autoComplete="off"
            value={customTip}
            onChange={(e) => setCustomTip(e.target.value)}
            className="h-9 w-20 rounded-full border border-input bg-background px-3 text-sm text-foreground outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
          />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-3">
        <Stat label="Tip" value={result?.tipAmount} />
        <Stat label="Total" value={result?.total} />
        <Stat label="Per person" value={result?.perPerson} highlight />
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }: { label: string; value?: number; highlight?: boolean }) {
  return (
    <div className={`rounded-xl px-3 py-4 text-center ${highlight ? 'bg-primary/10' : 'bg-muted'}`}>
      <p className={`text-xl font-bold ${highlight ? 'text-primary' : 'text-foreground'}`}>
        {value !== undefined ? `$${value.toFixed(2)}` : '—'}
      </p>
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
    </div>
  );
}
